import { useState } from "react";
import type { PrimitivesInfo, SimulationDef } from "../types";
import { FormEditor } from "./FormEditor";
import { JsonTab } from "./JsonTab";
import { LibraryDrawer } from "./LibraryDrawer";

export interface EditorPaneProps {
  readOnly?: boolean;
  definition: SimulationDef;
  definitionName: string | null;
  dirty: boolean;
  primitives: PrimitivesInfo | null;
  errors: string[];
  parseError: string | null;
  onEdit: (definition: SimulationDef) => void;
  onParseError: (error: string | null) => void;
  onOpen: (raw: Record<string, unknown>, name: string | null) => void;
  onToast: (text: string) => void;
}

export function EditorPane({
  readOnly = false,
  definition,
  definitionName,
  dirty,
  primitives,
  errors,
  parseError,
  onEdit,
  onParseError,
  onOpen,
  onToast,
}: EditorPaneProps) {
  const [tab, setTab] = useState<"form" | "json">("form");
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <section className="editor-pane">
      <nav className="tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === "form"}
          className={tab === "form" ? "active" : ""}
          onClick={() => setTab("form")}
        >
          Form
        </button>
        <button
          role="tab"
          aria-selected={tab === "json"}
          className={tab === "json" ? "active" : ""}
          onClick={() => setTab("json")}
        >
          JSON
        </button>
        <button className="tab-action" onClick={() => setDrawerOpen(true)}>
          Library
        </button>
      </nav>
      <div className="editor-body">
        {/* Both tabs stay mounted so neither loses editor state when switching. */}
        <div style={{ display: tab === "form" ? "block" : "none", height: "100%" }}>
          <FormEditor definition={definition} primitives={primitives} onChange={onEdit} />
        </div>
        <div style={{ display: tab === "json" ? "block" : "none", height: "100%" }}>
          <JsonTab definition={definition} onChange={onEdit} onParseError={onParseError} parseError={parseError} />
        </div>
      </div>
      {(parseError || errors.length > 0) && (
        <div className="diagnostics">
          {parseError && <div className="diagnostic">{parseError}</div>}
          {!parseError &&
            errors.map((error, index) => (
              <div key={index} className="diagnostic">
                {error}
              </div>
            ))}
        </div>
      )}
      {drawerOpen && (
        <LibraryDrawer
          readOnly={readOnly}
          definition={definition}
          definitionName={definitionName}
          dirty={dirty}
          onOpen={(raw, name) => {
            onOpen(raw, name);
            setDrawerOpen(false);
          }}
          onClose={() => setDrawerOpen(false)}
          onToast={onToast}
        />
      )}
    </section>
  );
}
